import React, { useEffect, memo } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import InfoModalStore from "@stores/InfoModalStore";
import ReportsModalStore from "@stores/ReportsModalStore";
import DownloadModalStore from "@stores/DownloadModalStore";

import Sidebar from "@components/SideBar/AppSidebar";
import "./App.css";
import ReconciliationRunsComponent from "@components/ReconciliationRunComponent";
import ReconciliationStreamsComponent from "@components/ReconciliationStreamsComponent";
import DataAcquisitions from "@components/DataAcquisitionsComponent";
import DataAcquisitionsInstances from "@components/DataAcquisitionsInstancesComponent";
import ReconciliationDashboard from "@components/ReconciliationDashboard";
import InTransitListComponent from "@components/InTransitListComponent";
import ExceptionsRecordComponent from "@components/ExceptionsRecordComponent";
import DashBoard from "@components/DashBoard";
import SfmModal from "@modals/SfmModal";
import InfoModal from "@modals/InfoModal";
import ReportsModal from "@modals/ReportsModal";
import DownloadModal from "@modals/DownloadModal";
import AttemptDetails from "@components/AttemptDetails";
import RunMatchingRecords from "@components/RunMatchingRecords";
import AttemptMatching from "@components/AttemptMatching";
import NewMatching from "@components/NewMatching";
import GlobalStore from "@stores/GlobalStore";
import BusinessDayComponent from "@components/BusinessDayComponent";
import ReconciliationSummary from "@components/ReconciliationSummaryComponent";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import clsx from "clsx";
import Menus from "./Menus";
import LoginPage from "@components/Login/LoginPage";
import ChartComponent from "@components/ChartComponent";
import CustomChartComponent from "@components/CustomChartComponent";

import { ErrorBoundary } from "react-error-boundary";
import BoundaryFallback from "@components/ErrorBoundaryComponent";

import { SidebarProvider, useSidebar } from "@stores/SidebarContext";

import AppHeader from "@components/AppHeader/AppHeader";
import { ThemeProvider } from "@stores/ThemeContext";

const usePageChange = (callback) => {
  const location = useLocation();

  useEffect(() => {
    callback(location);
  }, [location, callback]);
};

const MainContent = memo(({ isLoginPage, children }) => {
  const { isExpanded, isHovered, isMobileOpen } = useSidebar();

  const mainContentClassName = React.useMemo(() => {
    return `flex-1 transition-all duration-300 ease-in-out xl:w-[calc(100%-290px)] dark:bg-gray-800 ${
      isLoginPage
        ? "ml-0"
        : isExpanded || isHovered
        ? "lg:ml-[290px]"
        : "lg:ml-[90px]"
    } ${isMobileOpen ? "ml-0" : ""} ${isLoginPage && "!pl-0 !pr-0 !py-0"}`;
  }, [isLoginPage, isExpanded, isHovered, isMobileOpen]);

  return (
    <div className={mainContentClassName}>
      {!isLoginPage && <AppHeader />}
      {children}
    </div>
  );
});

MainContent.displayName = 'MainContent';

const AppContent = memo(() => {
  const { t: translate, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!GlobalStore.isLoggedIn()) {
      location.pathname !== "/login" &&
        localStorage.setItem("lastLocation", location.pathname);
      navigate("/login");
    }
  }, []);
  
  useEffect(() => {
    document.documentElement.lang = i18n.language;
    document.documentElement.dir = i18n.language === "ar" ? "rtl" : "ltr";
    GlobalStore.setGlobalSettings({
      languageCode: i18n.language,
    });
  });

  const handlePageChange = (location) => {
    InfoModalStore.setShowing(false);
    ReportsModalStore.close();
    DownloadModalStore.close();
  };

  const isLoginPage = location.pathname === "/login";

  usePageChange(handlePageChange);

  return (
    <div className="min-h-screen xl:flex">
      <PageChangeHandler onPageChange={handlePageChange} />
      {!isLoginPage && (
        <ErrorBoundary FallbackComponent={BoundaryFallback}>
          <Sidebar menus={Menus} />
        </ErrorBoundary>
      )}
      <MainContent isLoginPage={isLoginPage}>
        <Routes>
          <Route path="/" element={<DashBoard />} />
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/business-day-information"
            element={<BusinessDayComponent />}
          />
          <Route
            path="/reconciliation-runs"
            element={<ReconciliationRunsComponent />}
          />
          <Route
            path="/reconciliation-streams"
            element={<ReconciliationStreamsComponent />}
          />
          <Route path="/data-acquisitions" element={<DataAcquisitions />} />
          <Route
            path="/data-acquisitions-instances"
            element={<DataAcquisitionsInstances />}
          />
          <Route
            path="/reconciliation-dashboard"
            element={<ReconciliationDashboard />}
          />
          <Route
            path="/in-transit-summary"
            element={
              <ReconciliationSummary
                ReconciliationSummaryTitle={translate(
                  "exceptions-record.summary-title"
                )}
                ReconciliationSummaryTag="INTRANSIT"
                redirectionURL="/in-transit-list"
                headers={[
                  {
                    label: translate("shared.total-records"),
                    mapping: "INTRANSIT_RECORDS",
                    amountClassName:
                      "text-14 dark:bg-gray-700 bg-gray-100 p-[5px] rounded-[5px]",
                  },
                  {
                    label: translate("shared.critical-records"),
                    mapping: "INTRANSIT_CRITICAL",
                    className: "text-status-CRITICAL",
                    amountClassName: "text-14 bg-red-50 p-[5px] rounded-[5px]",
                    redirectionFilter: "&IsInTransitCritical=true",
                  },
                ]}
                wordToAdd="critical"
              />
            }
          />
          <Route path="/in-transit-list" element={<InTransitListComponent />} />
          <Route
            path="/exception-summary"
            element={
              <ReconciliationSummary
                ReconciliationSummaryTitle={translate(
                  "exceptions-record.summary-title"
                )}
                ReconciliationSummaryTag="EXCEPTION"
                redirectionURL="/exception-list"
                headers={[
                  {
                    label: translate("shared.total-records"),
                    mapping: "EXCEPTIONS_RECORDS",
                    amountClassName:
                      "text-14 bg-gray-100 p-[5px] rounded-[5px] dark:bg-gray-700",
                  },
                  {
                    label: translate("shared.aging-records"),
                    mapping: "AGING_EXCEPTIONS",
                    className: "text-status-CRITICAL",
                    amountClassName: "text-14 bg-red-50 p-[5px] rounded-[5px]",
                    redirectionFilter:
                      "&AgingTierTag=EXCEPTION_LOW_AGING_THRESHOLD_IN_HOURS,EXCEPTION_MODERATE_AGING_THRESHOLD_IN_HOURS,EXCEPTION_CRITICAL_AGING_THRESHOLD_IN_HOURS",
                  },
                ]}
                wordToAdd="aging"
              />
            }
          />
          <Route
            path="/exception-list"
            element={<ExceptionsRecordComponent />}
          />
          <Route
            path="/exception-attempt-details"
            element={
              <AttemptDetails
                sentActivityTag="GetExceptionAttempts"
                from="exception"
                redirectionURL="/exception-list"
              />
            }
          />
          <Route
            path="/in-transit-details"
            element={
              <AttemptDetails
                sentActivityTag="GetInTransitAttempts"
                from="transit"
                redirectionURL="/in-transit-list"
              />
            }
          />
          <Route
            path="/run-matching-records"
            element={<RunMatchingRecords />}
          />
          <Route
            path="/exception-attempt-matching"
            element={<AttemptMatching />}
          />
          <Route path="/tenant-hierarchy" element={<NewMatching />} />
          <Route
            path="/chart-test"
            element={<CustomChartComponent title="Chart tester" />}
          />
        </Routes>
      </MainContent>
      <ToastContainer
        position="bottom-right"
        autoClose={3000}
        hideProgressBar
        newestOnTop={false}
        closeOnClick
        rtl={i18n.language === "ar"}
        pauseOnFocusLoss
        pauseOnHover
      />
      <SfmModal />
      <InfoModal />
      <ReportsModal />
      <DownloadModal />
    </div>
  );
});

AppContent.displayName = 'AppContent';

const PageChangeHandler = memo(({ onPageChange }) => {
  usePageChange(onPageChange);
  return null;
});

PageChangeHandler.displayName = 'PageChangeHandler';

const App = () => {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <SidebarProvider>
          <AppContent />
        </SidebarProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
};

export default App;
